import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import Drawer from "../../../Component/Drawer";
import Pill, { PillTone } from "../../../Component/Pill";
import { getRequest } from "../../../utils/api";
import { logout } from "../../../utils/auth";
import { SETTLE_STATUS, affiliateName, clientName, inr, paymentModeLabel } from "./AffiliateLayout";

interface PayoutDrawerProps {
  payoutId: string | null;
  // bump to reload after a status change made from the drawer
  refreshKey?: number;
  onClose: () => void;
  onChangeStatus: (payout: any, status: number) => void;
}

// One payout: amount, the affiliate's bank details for the transfer, and the commissions it pays (/affiliate/affiliate-payout-details/:id)
const PayoutDrawer = ({ payoutId, refreshKey, onClose, onChangeStatus }: PayoutDrawerProps) => {
  const [details, setDetails] = useState<any>(null);
  const [isLoader, setIsLoader] = useState(false);

  // Fetch Payout Details
  useEffect(() => {
    if (!payoutId) { setDetails(null); return; }
    const getDetails = async () => {
      try {
        setIsLoader(true);
        const response = await getRequest(`/affiliate/affiliate-payout-details/${payoutId}`);
        if (response.status === "success") {
          setDetails(response.data);
        } else if (response.status === "session_expired") {
          logout();
        } else {
          toast.error(response.message);
          onClose();
        }
      } catch (error) {
        toast.error("Something went wrong loading the payout!");
      } finally {
        setIsLoader(false);
      }
    };
    getDetails();
  }, [payoutId, refreshKey]);

  const copy = (label: string, value: string) => {
    navigator.clipboard?.writeText(value)
      .then(() => toast.success(`${label} copied`))
      .catch(() => toast.error("Could not copy"));
  };

  const p = details?.payout;
  const aff = p?.ap_affiliate_id;
  const bank = details?.bank;
  const commissions: any[] = details?.commissions || [];
  const [tone, label]: [PillTone, string] = p ? (SETTLE_STATUS[p.ap_status] || ["none", "Unknown"]) : ["none", ""];

  const CopyValue = ({ label: l, value }: { label: string; value?: string }) => (
    <div>
      <small>{l}</small>
      <b className="mono" style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {value || "—"}
        {value && <button type="button" title={`Copy ${l}`} onClick={() => copy(l, value)} style={{ color: "var(--muted)" }}><i className="ri-file-copy-line"></i></button>}
      </b>
    </div>
  );

  return (
    <Drawer
      show={!!payoutId}
      onClose={onClose}
      lead={<div className="cx-ic blue" style={{ width: 44, height: 44, fontSize: 20, borderRadius: 12 }}><i className="ri-bank-line"></i></div>}
      title={p ? `Payout #${p.ap_unique_id || p.ap_id}` : "Loading…"}
      subtitle={p ? <>{affiliateName(aff)}{p.ap_week != null ? ` · week ${p.ap_week}, ${p.ap_year}` : ""} · created {moment(p.createdAt).format("DD MMM YYYY")}</> : null}
      footer={p && <>
        {/* Paid can be undone (marked by mistake); Failed is final — its commissions went back to the affiliate */}
        {p.ap_status === 1 && (
          <button type="button" className="cx-btn cx-btn-light" onClick={() => onChangeStatus(p, 0)}><i className="ri-arrow-go-back-line"></i>Move to pending</button>
        )}
        <div className="sp"></div>
        {p.ap_status === 0 && <>
          <button type="button" className="cx-btn cx-btn-danger" onClick={() => onChangeStatus(p, 2)}><i className="ri-close-circle-line"></i>Mark failed</button>
          <button type="button" className="cx-btn cx-btn-signal" onClick={() => onChangeStatus(p, 1)}><i className="ri-check-line"></i>Mark paid</button>
        </>}
      </>}
    >
      {isLoader && !p ? (
        <div className="cx-empty"><i className="ri-loader-4-line"></i>Loading payout…</div>
      ) : p && <>
        <div className="cx-kv k3">
          <div><small>Amount</small><b className="mono" style={{ fontSize: 17 }}>{inr(p.ap_amount)}</b></div>
          <div><small>Status</small><b><Pill tone={tone}>{label}</Pill></b></div>
          <div><small>Commissions</small><b className="mono">{commissions.length}</b></div>
        </div>

        {p.ap_status === 2 && (
          <div className="cx-banner" style={{ marginTop: 12 }}>
            <i className="ri-information-line"></i>
            <div>This payout failed. Its commissions were returned to {affiliateName(aff)}'s available balance — they can request a new payout.</div>
          </div>
        )}

        <h6 className="cx-h6">Transfer to</h6>
        {bank ? (
          <div className="cx-kv">
            <div><small>Account holder</small><b>{bank.bank_account_holder_name}</b></div>
            <div><small>Bank</small><b>{bank.bank_name}</b></div>
            <CopyValue label="Account number" value={bank.bank_account_no} />
            <CopyValue label="IFSC" value={bank.bank_ifsc_code} />
          </div>
        ) : (
          <div className="cx-banner" style={{ marginBottom: 0 }}>
            <i className="ri-bank-line"></i>
            <div>{affiliateName(aff)} has not added bank details yet. Ask them to add them from the affiliate panel before paying.</div>
          </div>
        )}
        <div className="fs12 muted" style={{ marginTop: 8 }}>
          {aff?.affiliate_email}{aff?.affiliate_mobile ? ` · ${`${aff.affiliate_country_code || ""} ${aff.affiliate_mobile}`.trim()}` : ""}
        </div>

        {p.ap_status === 1 && p.ap_payment_mode && <>
          <h6 className="cx-h6">Payment</h6>
          <div className="cx-kv k3">
            <div><small>Mode</small><b>{paymentModeLabel(p.ap_payment_mode)}</b></div>
            <CopyValue label="Reference" value={p.ap_payment_reference} />
            <div><small>Paid on</small><b>{p.ap_paid_at ? moment(p.ap_paid_at).format("DD MMM YYYY, HH:mm") : "—"}</b></div>
          </div>
        </>}

        {p.ap_remark && <>
          <h6 className="cx-h6">Remark</h6>
          <div className="cx-diff" style={{ display: "block", whiteSpace: "pre-wrap" }}>{p.ap_remark}</div>
        </>}

        <h6 className="cx-h6">Commissions in this payout</h6>
        <div className="cx-card">
          <table className="cx-tbl">
            <thead>
              <tr>
                <th>Commission</th>
                <th>Client</th>
                <th>Date</th>
                <th className="r">Amount</th>
              </tr>
            </thead>
            <tbody>
              {commissions.length ? commissions.map(c => (
                <tr key={c._id}>
                  <td>
                    <span className="mono">#{c.ac_unique_id || c.ac_id}</span>
                    <div className="fs12 muted">{c.ac_commission_tier_id?.commission_title || ""}{c.ac_commission_rate ? ` · ${c.ac_commission_rate}%` : ""}</div>
                  </td>
                  <td>{clientName(c.ac_user_id)}</td>
                  <td className="fs12 muted">{moment(c.createdAt).format("DD MMM YYYY")}</td>
                  <td className="r mono">{inr(c.ac_amount)}</td>
                </tr>
              )) : (
                <tr><td colSpan={4}><div className="cx-empty" style={{ padding: 20 }}>No commissions linked to this payout.</div></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </>}
    </Drawer>
  );
};

export default PayoutDrawer;
